import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import Navbar from "./components/SharedComponents/Navbar";
import Footer from "./components/SharedComponents/Footer";
import CookieNotice from "./components/SharedComponents/CookieNotice";
import GlobalScrollTopButton from "./components/SharedComponents/GlobalScrollTopButton";
import LogIn from "./components/Auth/LogIn";
import LogInV2 from "./components/Auth/LogInV2";
import SignUp from "./components/Auth/SignUp";
import Home from "./components/Home";
import Venues from "./components/Venues/Venues";
import Articles from "./components/Articles/Articles";
import ArticlePage from "./components/Articles/ArticlePage";
import VenueShow from "./components/Venues/VenueShow";
import ProductShow from "./components/Products/ProductShow";
import VenueDetails from "./components/Venues/VenueDetails";
import Photos from "./components/Photos/Photos";
import Profile from "./components/Users/Profile";
import FileUpload from "./components/Photos/UploadGuestPhotos";
import AllProductsPage from "./components/Products/AllProductsPage";
import ForgotPassword from "./components/Auth/ForgotPassword";
import Organizer from "./components/Organizer/Organizer";
import PrivateRoute from "./components/PrivateRoute";
import PublicRoute from "./components/PublicRoute";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import Contact from "./components/Contact/Contact";
import TermsAndConditions from "./components/TermsAndConditions/TermsAndConditions";
import InvitationsDashboard from "./components/Invitations/InvitationsDashboard";
import InvitationBuilder from "./components/Invitations/InvitationBuilder";
import InvitationPublicPage from "./components/Invitations/InvitationPublicPage";
import FullWidthInvitationPage from "./components/Invitations/FullWidthInvitationPage";
import TablesPrint from "./components/Organizer/Tables/TablesPrint";
import SeoManager from "./components/SEO/SeoManager";
import AdminDashboard from "./components/Admin/AdminDashboard";
import AdminSponsoredCampaignFormPage from "./components/Admin/AdminSponsoredCampaignFormPage";
import SaveTheDatesDashboard from "./components/SaveTheDates/SaveTheDatesDashboard";
import SaveTheDateBuilder from "./components/SaveTheDates/SaveTheDateBuilder";
import SaveTheDatePublicPage from "./components/SaveTheDates/SaveTheDatePublicPage";
import PricingPage from "./components/Pricing/PricingPage";
import CheckoutStatusPage from "./components/Pricing/CheckoutStatusPage";

function AppShell() {
  const location = useLocation();
  const { authPayload } = useAuth();

  // Hide chrome only on invitation pages for unauthenticated users
  const isInvite =
    location.pathname.startsWith("/invite/") ||
    location.pathname.startsWith("/pokana/") ||
    location.pathname.startsWith("/save-the-dates/");
  const isPrintPage = location.pathname === "/tables/print";
  const isAuthed = Boolean(authPayload?.id_token);
  const hideChrome = isPrintPage || (isInvite && !isAuthed);

  return (
    <>
      {!hideChrome && <Navbar authPayload={authPayload} />}
      <SeoManager />
      <ScrollToTop />
      <GlobalScrollTopButton />

      <Routes>
        <Route element={<PublicRoute />}>
          <Route path="/login/v1" element={<LogIn />} />
          <Route path="/login" element={<LogInV2 />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>

        <Route path="/" element={<Home />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/venues" element={<Venues />} />
        <Route path="/venue/:id" element={<VenueShow />} />
        <Route path="/product/:id" element={<ProductShow />} />
        <Route path="/all-products" element={<AllProductsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/checkout/success" element={<CheckoutStatusPage mode="success" />} />
        <Route path="/checkout/cancel" element={<CheckoutStatusPage mode="cancel" />} />
        <Route path="/upload-photos" element={<FileUpload />} />
        <Route path="/articles/:slug" element={<ArticlePage />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/invite/:slug" element={<InvitationPublicPage />} />
        <Route path="/pokana/:slug" element={<FullWidthInvitationPage />} />
        <Route
          path="/save-the-dates/:slug"
          element={<SaveTheDatePublicPage />}
        />

        <Route element={<PrivateRoute />}>
          <Route path="/organizer" element={<Organizer />} />
          <Route path="/venues/:venueId" element={<VenueDetails />} />
          <Route path="/photos" element={<Photos />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/tables/print" element={<TablesPrint />} />
          <Route path="/invitations" element={<InvitationsDashboard />} />
          <Route path="/invitations/new" element={<InvitationBuilder />} />
          <Route
            path="/invitations/:invitationId/edit"
            element={<InvitationBuilder />}
          />
          <Route path="/save-the-dates" element={<SaveTheDatesDashboard />} />
          <Route path="/save-the-dates/new" element={<SaveTheDateBuilder />} />
          <Route
            path="/save-the-dates/:saveTheDateId/edit"
            element={<SaveTheDateBuilder />}
          />
          <Route path="/dashboard" element={<AdminDashboard />} />
          <Route
            path="/dashboard/sponsored-campaigns/new"
            element={<AdminSponsoredCampaignFormPage />}
          />
          <Route
            path="/dashboard/sponsored-campaigns/:postId/edit"
            element={<AdminSponsoredCampaignFormPage />}
          />
        </Route>
      </Routes>

      {!hideChrome && <CookieNotice />}
      {!hideChrome && <Footer />}
    </>
  );
}

function AppRouter() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default AppRouter;
